import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatasetStore } from '@/store/dataset-store';
import { useCompareConfigStore } from '@/store/compare-config-store';
import { getQueryWorker } from '@/engine/worker-client';
import { isDateType } from '@/features/filters/column-types';
import type {
  CompareGranularity,
  ComparePeriod,
} from '@/persistence/config-db';

/**
 * Compare-mode configuration panel (D-020 step 2).
 *
 * Layout follows the user-supplied mockup:
 *   - granularity radio group (Aylık / Çeyrek / Yarıyıl / Yıllık)
 *   - explicit Period A / Period B selectors (year + sub-period)
 *   - Karşılaştır / Temizle buttons
 *
 * Year list is auto-derived from the active dataset's date column via
 * the worker's `getDateColumnYearRange` RPC. The user picks the date
 * column from the dropdown — only DATE / TIMESTAMP columns appear.
 *
 * Renders inline in compare mode (rendered by `App.tsx` between the
 * compare banner and the dashboard grid). Hidden when a dataset is
 * not active.
 */
export function CompareConfigPanel(): JSX.Element | null {
  const { t } = useTranslation();
  const activeId = useDatasetStore((s) => s.activeDatasetId);
  const datasets = useDatasetStore((s) => s.datasets);
  const config = useCompareConfigStore((s) => s.config);
  const loadedFor = useCompareConfigStore((s) => s.datasetId);
  const loadConfig = useCompareConfigStore((s) => s.loadForDataset);
  const saveConfig = useCompareConfigStore((s) => s.save);
  const clearConfig = useCompareConfigStore((s) => s.clear);

  const entry = activeId ? datasets.get(activeId) : undefined;
  // Depend on entry.columns (not the whole entry) — setDatasetProfile /
  // setValueGroupings produce a fresh entry reference even when columns
  // are unchanged, which used to re-fire every downstream effect
  // (including the date-column auto-pick and year-range fetch) on every
  // background update and contributed to render thrashing after upload.
  const dateColumns = useMemo(
    () => (entry?.columns ?? []).filter((c) => isDateType(c.sqlType)),
    [entry?.columns],
  );

  // Hydrate persisted config when the active dataset changes.
  useEffect(() => {
    if (activeId && loadedFor !== activeId) {
      void loadConfig(activeId);
    }
  }, [activeId, loadedFor, loadConfig]);

  // Local draft mirrors the persisted config; "Karşılaştır" commits.
  const [dateColumn, setDateColumn] = useState<string>('');
  const [granularity, setGranularity] =
    useState<CompareGranularity>('monthly');
  const [periodA, setPeriodA] = useState<ComparePeriod>({ year: 0, sub: 1 });
  const [periodB, setPeriodB] = useState<ComparePeriod>({ year: 0, sub: 1 });
  const [years, setYears] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  // Seed local draft from the persisted config when it loads / changes.
  useEffect(() => {
    if (config) {
      setDateColumn(config.dateColumn);
      setGranularity(config.granularity);
      setPeriodA(config.periodA);
      setPeriodB(config.periodB);
    } else {
      setDateColumn(dateColumns[0]?.name ?? '');
    }
  }, [config, dateColumns]);

  // Refresh year range whenever the date column changes. Worker call;
  // results are tiny so no caching needed.
  useEffect(() => {
    if (!activeId || !dateColumn) {
      setYears([]);
      return;
    }
    let cancelled = false;
    void getQueryWorker()
      .getDateColumnYearRange(activeId, dateColumn)
      .then((range) => {
        if (cancelled || !range) return;
        // Sanity-clamp the range. A bad cell with year 9999 (or 0) in the
        // source Excel can push min/max to extremes; an unclamped loop
        // would generate thousands of <option> nodes and freeze the
        // browser on render. 200 years is more than any realistic
        // business dataset spans.
        const MAX_YEARS = 200;
        const safeMin = Math.max(range.min, range.max - MAX_YEARS + 1);
        const list: number[] = [];
        for (let y = range.max; y >= safeMin; y--) list.push(y);
        setYears(list);
        // Default both periods to the most recent year if user hasn't picked.
        setPeriodA((p) =>
          p.year && list.includes(p.year) ? p : { year: list[0]!, sub: p.sub ?? 1 },
        );
        setPeriodB((p) =>
          p.year && list.includes(p.year)
            ? p
            : { year: list[1] ?? list[0]!, sub: p.sub ?? 1 },
        );
      })
      .catch((err: unknown) => {
        console.warn('[compare] getDateColumnYearRange failed', err);
      });
    return (): void => {
      cancelled = true;
    };
  }, [activeId, dateColumn]);

  if (!entry || !activeId) return null;

  if (dateColumns.length === 0) {
    return (
      <div
        className="mb-3 px-3 py-2 rounded text-xs"
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          color: 'var(--color-muted)',
        }}
      >
        {t('mode.panel.noDateColumns')}
      </div>
    );
  }

  const handleCompare = async (): Promise<void> => {
    if (!dateColumn || !periodA.year || !periodB.year) return;
    setBusy(true);
    try {
      // Strip `sub` for yearly granularity so the persisted shape is clean.
      const stripIfYearly = (p: ComparePeriod): ComparePeriod =>
        granularity === 'yearly' ? { year: p.year } : p;
      await saveConfig({
        datasetId: activeId,
        dateColumn,
        granularity,
        periodA: stripIfYearly(periodA),
        periodB: stripIfYearly(periodB),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-3 p-3 rounded"
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div
        className="text-xs uppercase tracking-wide font-semibold mb-2"
        style={{ color: 'var(--color-muted)' }}
      >
        {t('mode.panel.title')}
      </div>

      {/* Date column picker */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <label
          htmlFor="compare-date-col"
          className="text-xs"
          style={{ color: 'var(--color-fg)' }}
        >
          {t('mode.panel.dateColumn')}:
        </label>
        <select
          id="compare-date-col"
          value={dateColumn}
          onChange={(e) => setDateColumn(e.target.value)}
          className="text-xs rounded border px-2 py-1"
          style={{
            background: 'var(--color-bg)',
            color: 'var(--color-fg)',
            borderColor: 'var(--color-border)',
          }}
        >
          {dateColumns.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Granularity radio group */}
      <div
        className="inline-flex rounded overflow-hidden mb-3"
        style={{ border: '1px solid var(--color-border)' }}
        role="radiogroup"
        aria-label={t('mode.panel.title')}
      >
        {(['monthly', 'quarterly', 'semiannual', 'yearly'] as const).map((g) => {
          const active = granularity === g;
          return (
            <button
              key={g}
              type="button"
              aria-pressed={active}
              onClick={() => setGranularity(g)}
              className="text-xs px-3 py-1.5 transition-colors"
              style={{
                background: active ? 'var(--color-accent)' : 'var(--color-bg)',
                color: active ? '#fff' : 'var(--color-fg)',
              }}
            >
              {t(`mode.granularity.${g}`)}
            </button>
          );
        })}
      </div>

      {/* Period A vs Period B pickers */}
      <div className="flex items-end gap-3 flex-wrap mb-3">
        <PeriodPicker
          label={
            granularity === 'yearly' ? t('mode.panel.yearA') : t('mode.panel.periodA')
          }
          accent="primary"
          period={periodA}
          onChange={setPeriodA}
          granularity={granularity}
          years={years}
        />
        <span
          className="px-1 pb-1 text-sm font-bold"
          style={{ color: 'var(--color-muted)' }}
        >
          {t('mode.panel.vs')}
        </span>
        <PeriodPicker
          label={
            granularity === 'yearly' ? t('mode.panel.yearB') : t('mode.panel.periodB')
          }
          accent="warning"
          period={periodB}
          onChange={setPeriodB}
          granularity={granularity}
          years={years}
        />
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy || !dateColumn || years.length === 0}
          onClick={() => void handleCompare()}
          className="text-xs px-3 py-1.5 rounded text-white disabled:opacity-50"
          style={{ background: 'var(--color-accent)' }}
        >
          {t('mode.panel.compare')}
        </button>
        <button
          type="button"
          disabled={busy || !config}
          onClick={() => void clearConfig()}
          className="text-xs px-3 py-1.5 rounded disabled:opacity-50"
          style={{
            color: 'var(--color-muted)',
            border: '1px solid var(--color-border)',
          }}
        >
          {t('mode.panel.clear')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PeriodPicker
// ---------------------------------------------------------------------------

type PeriodPickerProps = {
  label: string;
  accent: 'primary' | 'warning';
  period: ComparePeriod;
  onChange: (next: ComparePeriod) => void;
  granularity: CompareGranularity;
  years: number[];
};

function PeriodPicker({
  label,
  accent,
  period,
  onChange,
  granularity,
  years,
}: PeriodPickerProps): JSX.Element {
  const { t } = useTranslation();

  const subOptions = useMemo<{ value: number; label: string }[]>(() => {
    if (granularity === 'monthly') {
      return Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: t(`mode.month.${i + 1}`),
      }));
    }
    if (granularity === 'quarterly') {
      return [1, 2, 3, 4].map((q) => ({
        value: q,
        label: t(`mode.quarter.${q}`),
      }));
    }
    if (granularity === 'semiannual') {
      return [1, 2].map((h) => ({
        value: h,
        label: t(`mode.half.${h}`),
      }));
    }
    return [];
  }, [granularity, t]);

  const subLabel =
    granularity === 'monthly'
      ? t('mode.panel.month')
      : granularity === 'quarterly'
      ? t('mode.panel.quarter')
      : granularity === 'semiannual'
      ? t('mode.panel.halfyear')
      : null;

  const badgeBg =
    accent === 'primary'
      ? 'var(--color-accent)'
      : 'color-mix(in srgb, #d97706 80%, var(--color-bg))';

  return (
    <div className="flex flex-col gap-1">
      <span
        className="self-start text-[10px] px-2 py-0.5 rounded font-semibold"
        style={{ background: badgeBg, color: '#fff' }}
      >
        {label}
      </span>
      <div className="flex gap-2">
        <div className="flex flex-col gap-0.5">
          <label
            className="text-[10px] font-semibold"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('mode.panel.year')}
          </label>
          <select
            value={period.year || ''}
            onChange={(e) =>
              onChange({ ...period, year: Number(e.target.value) })
            }
            className="text-xs rounded border px-2 py-1"
            style={{
              background: 'var(--color-bg)',
              color: 'var(--color-fg)',
              borderColor: 'var(--color-border)',
              minWidth: '88px',
            }}
          >
            {years.length === 0 && <option value="">—</option>}
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        {subLabel && (
          <div className="flex flex-col gap-0.5">
            <label
              className="text-[10px] font-semibold"
              style={{ color: 'var(--color-muted)' }}
            >
              {subLabel}
            </label>
            <select
              value={period.sub ?? 1}
              onChange={(e) =>
                onChange({ ...period, sub: Number(e.target.value) })
              }
              className="text-xs rounded border px-2 py-1"
              style={{
                background: 'var(--color-bg)',
                color: 'var(--color-fg)',
                borderColor: 'var(--color-border)',
                minWidth: granularity === 'monthly' ? '110px' : '140px',
              }}
            >
              {subOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
